'use client'
import { useEffect, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import CustomFetch from "@/Utils/APIRequest/AxiosInstance";
import { AdminUserListAPI } from "@/Utils/APIRequest/Endpoints";
import { useTranslation } from "@/Utils/Hooks/useTranslate";
import DetailHeader from "./DetailHeader";
import DetailTabs from "./DetailTabs";
import DetailInstagramPosts from "./SocailMediaPosts/DetailInstagramPosts";
import { useLoading } from "@/Utils/Hooks/useLoading";
import ImageWithFallback from "../ImageWithFallback";
import { AppStore } from "@/lib/store";
import InfluencerDetailSkeleton from "./InfluencerDetailSkeleton";
import { getInfluencerDetails, setLoading } from "@/lib/Features/InfluencerDataSlice/influencerDataSlice";

const getDefaultFilteredValue = (parsed = {}) => ({
    skills: [],
    keywords: [],
    ...parsed, // parsed values will overwrite defaults if present
});

const InfluencerDetail = (props) => {
    const { slug, setOpenSideModal, scrollRef, openSideModal, setPoolActiveTab, filteredValue, setFilteredValue, setRestrictModalOpen, campaignDetailData, isSeen } = props;
    const { translations } = useTranslation();
    const pathName = usePathname();
    const searchQuery = useSearchParams();
    const campaignIdQuery = searchQuery.get("id");
    const [postId, setPostId] = useState('');
    const [socialMediaType, setSocialMediaType] = useState('');
    const { skeletonLoading, startSkeletonLoading, stopSkeletonLoading } = useLoading();
    const userDetails: any = useSelector((state: AppStore) => state.userDetailSlice?.userDetails);
    const dispatch = useDispatch();
    const router = useRouter();

    // Get API
    const { data: UserDetailData, fetchStatus, isPending } = useQuery({ queryKey: [AdminUserListAPI, slug, campaignIdQuery, isSeen], queryFn: () => CustomFetch({ url: `${AdminUserListAPI}/${slug}`, params: { campaign_id: campaignIdQuery, ...(isSeen ? { is_seen: true } : {}) } }, null, false), enabled: userDetails ? true : false, refetchOnWindowFocus: false, select: (data) => data?.data?.data });

    useEffect(() => {
        if (isPending) {
            dispatch(setLoading(true));
        } else {
            dispatch(getInfluencerDetails(UserDetailData));
            dispatch(setLoading(false));
        }
    }, [UserDetailData, isPending]);

    useEffect(() => {
        startSkeletonLoading();
    }, []);

    const handleClick = (item, type, filteredValue, setFilteredValue) => {
        const entry = {
            label: type === 'keywords'
                ? item?.translations?.[0]?.keyword
                : item?.translations?.[0]?.title,
            value: item?.id
        };

        if (!entry.label || !entry.value) return;

        // Use default structure always!
        const workingFilteredValue = getDefaultFilteredValue(filteredValue);

        const prevArr = Array.isArray(workingFilteredValue[type]) ? workingFilteredValue[type] : [];
        const exists = prevArr.some(x => x.value === entry.value);

        if (!exists) {
            const newFilteredValue = {
                ...workingFilteredValue,
                [type]: [...prevArr, entry],
            };
            setFilteredValue(newFilteredValue);
            sessionStorage.setItem('filteredValue', JSON.stringify(newFilteredValue));
        }

        if (pathName.includes('my-campaigns') && !pathName.includes('user')) {
            // sessionStorage.setItem('tag', value);

            setPoolActiveTab && setPoolActiveTab('Influencer Search');

            if (setOpenSideModal) {
                setOpenSideModal(null)
            };
            if (scrollRef && scrollRef.current) {
                scrollRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        }
        if ((pathName.includes('browse-influencer') && !pathName.includes('user'))) {
            // sessionStorage.setItem('influencer-tag', value);


            if (setOpenSideModal) {
                setOpenSideModal(null)
            };
            if (scrollRef && scrollRef.current) {
                scrollRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        }
        if ((!pathName.includes('browse-influencer') && !pathName.includes('user') && !pathName.includes('my-campaigns'))) {
            // sessionStorage.setItem('influencer-tag', value);
            router.push('/brand/browse-influencer/search');
            if (setOpenSideModal) {
                setOpenSideModal(null)
            };
            if (scrollRef && scrollRef.current) {
                scrollRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }

    const validCategories = UserDetailData?.influencerCategories?.filter(c => c?.category !== null);

    const dangerousTags = [
        'script',
        'style',
        'iframe',
        'embed',
        'object',
        'img',
        'video',
        'audio',
        'source',
        'link'
    ];

    const sanitizeHtml = (html) => {
        if (!html) return '';
        let result = html;
        dangerousTags.forEach(tag => {
            result = result.replace(new RegExp(`<${tag}[\\s\\S]*?>[\\s\\S]*?<\\/${tag}>`, 'gi'), '');
            result = result.replace(new RegExp(`<${tag}[^>]*?\\/?>`, 'gi'), '');
        });
        return result;
    };

    const formatHtml = (text) => {
        let sanitized = sanitizeHtml(text);
        // Convert URLs to clickable anchor tags
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const formattedText = sanitized?.replace(urlRegex, (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline">${url}</a>`)?.replace(/\n/g, "<br/>"); // Preserve line breaks

        return formattedText;
    };

    if (fetchStatus === "fetching" || isPending) { return <InfluencerDetailSkeleton /> }

    return (
        <div className="border border-lightGray rounded-2xl overflow-hidden">
            <DetailHeader UserDetailData={UserDetailData} translations={translations} userDetails={userDetails} openSideModal={openSideModal} setOpenSideModal={setOpenSideModal} setRestrictModalOpen={setRestrictModalOpen} campaignDetailData={campaignDetailData} />
            <div className="md:py-10 py-5 px-5">
                <div className="grid grid-cols-12 gap-3">
                    <div className="md:col-span-8 col-span-12">
                        <div className="border-b border-lightGray pb-5">
                            {UserDetailData?.about_me && <> <h3 className="mb-2 text-lg font-bold">{translations?.about}</h3>
                                <p className="text-sm" dangerouslySetInnerHTML={{ __html: UserDetailData?.about_me ? formatHtml(UserDetailData?.about_me) : '-' }}></p>
                            </>
                            }
                            {UserDetailData?.ai_summary && <> <h3 className={`mb-2 text-lg font-bold ${UserDetailData?.about_me ? 'mt-5' : ''}`}>{translations?.aiSummary}</h3>
                                <p className="text-sm" dangerouslySetInnerHTML={{ __html: UserDetailData?.ai_summary ? formatHtml(UserDetailData?.ai_summary) : '-' }}></p>
                            </>
                            }
                            {validCategories?.length > 0 && (
                                <>
                                    <h3 className="mb-2 text-lg font-bold mt-5">{translations?.categoryHeaderTitle}</h3>
                                    <div className='flex flex-wrap gap-2.5'>
                                        {validCategories.map((category) => (
                                            <span
                                                onClick={() => handleClick(category?.category, 'skills', filteredValue, setFilteredValue)}
                                                className='block bg-gray/25 px-2.5 rounded-md cursor-pointer text-xs'
                                                key={category?.id}
                                            >
                                                {category?.category?.translations?.[0]?.title}
                                            </span>
                                        ))}
                                    </div>
                                </>
                            )}
                            {UserDetailData?.userKeywords.length > 0 &&
                                <>
                                    <h3 className="mb-2 text-lg font-bold mt-5">{translations?.tags}</h3>
                                    <div className='flex flex-wrap gap-2.5'>
                                        {UserDetailData?.userKeywords.map((keyword, index) => (
                                            <span onClick={() => handleClick(keyword?.keywords, 'keywords', filteredValue, setFilteredValue)} className='block bg-gray/25 px-2.5 rounded-md cursor-pointer text-xs' key={index}>{keyword?.keywords?.translations?.[0]?.keyword}</span>
                                        ))}
                                    </div>
                                </>
                            }

                        </div>
                        <div className="mt-4">
                            <DetailTabs UserDetailData={UserDetailData} setPostId={setPostId} setSocialMediaType={setSocialMediaType} socialMediaType={socialMediaType} translations={translations} />
                        </div>
                    </div>
                    <div className="md:col-span-4 col-span-12">
                        <div className="md:border md:border-lightGray md:p-5 rounded-2xl min-h-[250px] xl:min-h-[350px] max-md:mt-5">
                            <h3 className="text-lg font-bold md:mb-7 mb-5">{translations?.topPost}</h3>
                            {(UserDetailData?.socialMediaPosts?.length > 0) ? (
                                <div className='grid grid-cols-3 gap-2.5'>
                                    {UserDetailData?.socialMediaPosts?.map((post, index) => (
                                        <div className='w-full overflow-hidden aspect-square bg-gray/20 relative cursor-pointer' key={index} onClick={() => { window.open(post?.post_url, "_blank") }}>
                                            <ImageWithFallback src={post?.image_url ?? post?.thumbnails ?? post?.thumbnail_url} alt="Post" width={298} height={298} className='w-full h-full object-cover' />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <span className="bg-gray/20 w-full h-full flex items-center justify-center p-5 rounded-xl text-base font-semibold text-gray">{translations?.noPostFound}</span>
                            )}
                        </div>
                    </div>
                </div>
                <h3 className="text-lg font-bold mt-8 md:mb-7 mb-5">{translations?.allPosts}</h3>
                {socialMediaType === 'instagram' && <DetailInstagramPosts mediaData={UserDetailData?.social_medias?.find(media => media.social_type === 'instagram')} postId={postId} setPostId={setPostId} skeletonLoading={skeletonLoading} stopSkeletonLoading={stopSkeletonLoading} params={{ social_type: 'instagram', type: 'recent', likeKey: "like_count", imageKey: "thumbnail_url", url: "permalink", created_at: "instagram_timestamp" }} key={socialMediaType} translations={translations} socialMediaType={socialMediaType} />}

                {socialMediaType === 'facebook' && <DetailInstagramPosts mediaData={UserDetailData?.social_medias?.find(media => media.social_type === 'facebook')} postId={postId} setPostId={setPostId} skeletonLoading={skeletonLoading} stopSkeletonLoading={stopSkeletonLoading} params={{ social_type: 'facebook', type: 'recent', likeKey: "like_counts", imageKey: "thumbnails", url: "post_url", created_at: "created_time" }} key={socialMediaType} translations={translations} socialMediaType={socialMediaType} />}

                {socialMediaType === 'youtube' && <DetailInstagramPosts mediaData={UserDetailData?.social_medias?.find(media => media.social_type === 'youtube')} postId={postId} setPostId={setPostId} skeletonLoading={skeletonLoading} stopSkeletonLoading={stopSkeletonLoading} params={{ social_type: 'youtube', type: 'recent', likeKey: "view_counts", imageKey: "thumbnails", url: "post_url", created_at: "published_at" }} key={socialMediaType} translations={translations} socialMediaType={socialMediaType} />}

                {socialMediaType === 'tiktok' && <DetailInstagramPosts mediaData={UserDetailData?.social_medias?.find(media => media.social_type === 'tiktok')} postId={postId} setPostId={setPostId} skeletonLoading={skeletonLoading} stopSkeletonLoading={stopSkeletonLoading} params={{ social_type: 'tiktok', type: 'recent', likeKey: "view_count", imageKey: "cover_image_url", url: "share_url", created_at: "create_time" }} key={socialMediaType} translations={translations} socialMediaType={socialMediaType} />}
            </div>
        </div>
    )
}

export default InfluencerDetail
